// ProductList.js

import React, { useMemo, useState } from "react";
import { useTable, usePagination } from "react-table";
import { Row, Col, Button, Table, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getDatabase, ref, update, get } from "firebase/database";
import { toast } from "react-toastify";
import { ThreeDots } from "react-loader-spinner";
import { duplicateListing, formatListingDate } from "./utils";
import { notifyOwnerBoatApproved } from "./utils/userNotifications";
import { normalizeAssetUrl } from "./utils/assetUrl";
import UserAvatar from "./UserAvatar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

// Icons 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy, faBan, faCheckCircle, faEdit, faSearch } from "@fortawesome/free-solid-svg-icons";

const ProductList = ({ data, columns, getData, isAdmin, showDateColumn }) => {
  const [updateListingLoading, setUpdateListingLoading] = useState(false);
  const [selectedBoat, setSelectedBoat] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [boatToDuplicate, setBoatToDuplicate] = useState(null);

  // Filters State
  const [filterCity, setFilterCity] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterOwner, setFilterOwner] = useState("");
  const [filterBoatType, setFilterBoatType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return "https://images.pexels.com/photos/19415811/pexels-photo-19415811/free-photo-of-back-view-of-man-in-trench-coat-and-hat.jpeg?auto=compress&cs=tinysrgb&w=300";
    if (imageUrl.includes("localhost:3000/images/") && process.env.REACT_APP_BASE_URL) {
      return imageUrl.replace(/^https?:\/\/localhost:3000/, process.env.REACT_APP_BASE_URL);
    }
    return normalizeAssetUrl(imageUrl);
  };

  // Extract unique values for filter dropdowns
  const uniqueCities = [...new Set(data.map(item => item.city).filter(Boolean))].sort();
  const uniqueStates = [...new Set(data.map(item => item.state).filter(Boolean))].sort();
  const uniqueOwners = [...new Set(data.map(item => item.ownerName).filter(Boolean))].sort();
  const uniqueBoatTypes = [...new Set(data.map(item => item.boatType).filter(Boolean))].sort();

  // Custom cell renderers for table
  const tableColumns = useMemo(
    () => [
      {
        Header: "BOAT LISTING",
        accessor: "boatListing",
        Cell: ({ row }) => {
          const { id, name, year, backgroundImage } = row.original;
          return (
            <div className="d-flex align-items-center gap-3">
              <Link to={`/listing/${id}`}>
                <div
                  style={{
                    width: "80px",
                    height: "55px",
                    borderRadius: "8px",
                    backgroundImage: `url(${getImageUrl(backgroundImage)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    border: "1px solid #dee2e6"
                  }}
                />
              </Link>
              <div className="d-flex flex-column">
                <Link to={`/listing/${id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <strong style={{ fontSize: "15px", color: "#1e293b" }}>{name}</strong>
                </Link>
                <span style={{ fontSize: "13px", color: "#64748b" }}>
                  ID: B-{id.substring(id.length - 5).toUpperCase()} • {year !== "N/A" ? `${year} Model` : ""}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        Header: "STATUS",
        accessor: "status",
        Cell: ({ value }) => {
          let badgeColor = "#e2e8f0";
          let textColor = "#475569";
          let dotColor = "#94a3b8";

          // Depending on status values: "Active", "Pending", "Reject" 
          if (value?.toLowerCase() === "active" || (typeof value === "object" && value?.featured)) {
            badgeColor = "#dcfce7";
            textColor = "#166534";
            dotColor = "#22c55e";
          } else if (value?.toLowerCase() === "pending") {
            badgeColor = "#fef08a";
            textColor = "#854d0e";
            dotColor = "#eab308";
          } else if (value?.toLowerCase() === "reject") {
            badgeColor = "#fee2e2";
            textColor = "#991b1b";
            dotColor = "#ef4444";
          } else if (typeof value === "string") {
            // Default styling based on string value
            if (value?.includes("Active") || value?.includes("Featured")) {
              badgeColor = "#dcfce7"; textColor = "#166534"; dotColor = "#22c55e";
            } else if (value?.includes("Pending")) {
              badgeColor = "#fef08a"; textColor = "#854d0e"; dotColor = "#eab308";
            }
          }

          const displayValue = typeof value === "object" ? (value.featured ? "Featured" : "Not Featured") : value || "Pending";
          const formattedStatus = displayValue?.toLowerCase() === 'pending review' || displayValue?.toLowerCase() === 'pending' ? 'Pending' : displayValue;

          return (
            <div
              style={{
                backgroundColor: badgeColor,
                color: textColor,
                padding: "6px 12px",
                borderRadius: "20px",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                fontWeight: "600",
                whiteSpace: "nowrap"
              }}
            >
              <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }}></div>
              {formattedStatus}
            </div>
          );
        }
      },
      ...(showDateColumn
        ? [
            {
              Header: "DATE",
              accessor: "submittedAt",
              Cell: ({ value }) => (
                <span style={{ fontSize: "14px", color: "#475569", whiteSpace: "nowrap" }}>
                  {formatListingDate(value)}
                </span>
              ),
            },
          ]
        : []),
      ...(isAdmin ? [{
        Header: "OWNER",
        accessor: "owner",
        Cell: ({ row }) => {
          const { ownerId, ownerName, ownerAvatar, ownerInitials } = row.original;
          return (
            <Link to={`/dashboard/user/${ownerId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="d-flex align-items-center gap-2">
                <UserAvatar
                  src={ownerAvatar}
                  name={ownerName}
                  size={32}
                  style={{ backgroundColor: "#334155", fontSize: "13px" }}
                />
                <strong style={{ fontSize: "14px", color: "#334155" }}>{ownerName}</strong>
              </div>
            </Link>
          );
        }
      }] : []),
      {
        Header: "ACTIONS",
        accessor: "actions",
        Cell: ({ row }) => {
          const { id, name, boatListed } = row.original;
          return (
            <div className="d-flex gap-2">
              <Button
                as={Link}
                to={`/dashboard/listing/${id}`}
                variant="light"
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: "6px", padding: "6px 12px", fontSize: "13px", backgroundColor: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", textDecoration: "none" }}
              >
                <FontAwesomeIcon icon={faEdit} /> Edit
              </Button>
              <Button
                variant="primary"
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{ borderRadius: "6px", padding: "6px 12px", fontSize: "13px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setBoatToDuplicate({ id, name });
                  setDuplicateOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faCopy} /> Duplicate
              </Button>
              <Button
                variant={boatListed ? "danger" : "success"}
                size="sm"
                className="d-flex align-items-center gap-1"
                style={{
                  borderRadius: "6px",
                  padding: "6px 12px",
                  fontSize: "13px",
                  backgroundColor: boatListed ? "#fee2e2" : "#dcfce7",
                  color: boatListed ? "#ef4444" : "#22c55e",
                  border: "none"
                }}
                disabled={updateListingLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setSelectedBoat(row.original);
                  setOpen(true);
                }}
              >
                <FontAwesomeIcon icon={boatListed ? faBan : faCheckCircle} /> {boatListed ? "Disable" : "Enable"}
              </Button>
            </div>
          );
        }
      }
    ],
    [isAdmin, showDateColumn, updateListingLoading]
  );

  // Filter Data
  const filteredData = useMemo(() => {
    return data.filter(item => {
      // General Search
      const searchMatch = !search || Object.values(item).some(val =>
        val?.toString().toLowerCase().includes(search.toLowerCase())
      );

      // Select Filters
      const cityMatch = !filterCity || item.city === filterCity;
      const stateMatch = !filterState || item.state === filterState;
      const ownerMatch = !filterOwner || item.ownerName === filterOwner;
      const boatTypeMatch = !filterBoatType || item.boatType === filterBoatType;

      // Status Filter (Active, Pending)
      let statusMatch = true;
      if (filterStatus) {
        let itemStatus = typeof item.status === "object" ? "Active" : item.status || "Pending";
        statusMatch = itemStatus.toLowerCase().includes(filterStatus.toLowerCase());
      }

      return searchMatch && cityMatch && stateMatch && ownerMatch && boatTypeMatch && statusMatch;
    });
  }, [data, search, filterCity, filterState, filterOwner, filterBoatType, filterStatus]);

  // Sort Data
  const sortedAndFilteredData = useMemo(() => {
    let sorted = [...filteredData];
    if (sortOrder === "newest") {
      sorted.sort(
        (a, b) =>
          (b.submittedAt || 0) - (a.submittedAt || 0) ||
          String(b.id).localeCompare(String(a.id))
      );
    } else if (sortOrder === "oldest") {
      sorted.sort(
        (a, b) =>
          (a.submittedAt || 0) - (b.submittedAt || 0) ||
          String(a.id).localeCompare(String(b.id))
      );
    } else if (sortOrder === "alpha_asc") {
      sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortOrder === "alpha_desc") {
      sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    }
    return sorted;
  }, [filteredData, sortOrder]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns: tableColumns,
      data: sortedAndFilteredData,
      initialState: { pageIndex: 0, pageSize: 15 },
    },
    usePagination
  );

  const UpdateListingState = async (id, status) => {
    try {
      setUpdateListingLoading(true);
      const listingsRef = ref(getDatabase(), `listings/${id}`);

      await update(listingsRef, {
        boatListed: status,
        ...(status ? { boatStatus: "Active" } : {}),
      });

      if (status) {
        const snapshot = await get(listingsRef);
        const listing = snapshot.val();
        if (listing?.ownerId) {
          notifyOwnerBoatApproved(
            listing.ownerId,
            listing.listingTitle || "Your boat",
            id
          ).catch((err) =>
            console.error("Failed to create owner notification:", err)
          );
        }
      }

      if (typeof getData === "function") {
        await getData();
      }
      setUpdateListingLoading(false);
    } catch (error) {
      console.error("Error Updating Boat:", error);
      toast.error("Error Updating Boat");
    } finally {
      setUpdateListingLoading(false);
    }
  };

  const handleDuplicateBoat = async () => {
    if (!boatToDuplicate?.id) return;

    try {
      setDuplicateLoading(true);
      await duplicateListing(boatToDuplicate.id);
      toast.success("Boat duplicated successfully! The copy is set to Pending status.");
      await getData();
      setDuplicateOpen(false);
      setBoatToDuplicate(null);
    } catch (error) {
      console.error("Error duplicating boat:", error);
      toast.error("Error duplicating boat");
    } finally {
      setDuplicateLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterCity("");
    setFilterState("");
    setFilterOwner("");
    setFilterBoatType("");
    setFilterStatus("");
    setSearch("");
    setSortOrder("newest");
  };

  return (
    <div className="p-4">
      {/* Filter Section */}
      <div className="mb-4" style={{ backgroundColor: "#f8fafc", padding: "24px", borderRadius: "12px", border: "1px solid #f1f5f9" }}>
        {/* Row 1: Search, Status, Boat Type, Owner, State */}
        <div className="row g-3 mb-3">
          <div className="col-12 col-md-4">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Search</Form.Label>
            <div className="position-relative">
              <span className="position-absolute" style={{ top: "50%", transform: "translateY(-50%)", left: "14px", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faSearch} style={{ fontSize: "14px", fontWeight: "300" }} />
              </span>
              <Form.Control
                type="text"
                placeholder="Search by ID, name, or model..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="shadow-none"
                style={{ 
                  borderRadius: "8px", 
                  paddingLeft: "40px", 
                  fontSize: "13px", 
                  color: "#334155", 
                  borderColor: "#e2e8f0",
                  height: "38px"
                }}
              />
            </div>
          </div>

          <div className="col-6 col-md-2">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Status</Form.Label>
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="shadow-none"
              style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
            >
              <option value="">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending Review</option>
              <option value="Reject">Rejected</option>
            </Form.Select>
          </div>

          <div className="col-6 col-md-2">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Boat Type</Form.Label>
            <Form.Select
              value={filterBoatType}
              onChange={(e) => setFilterBoatType(e.target.value)}
              className="shadow-none"
              style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
            >
              <option value="">All Types</option>
              {uniqueBoatTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
          </div>

          {isAdmin && (
            <div className="col-6 col-md-2">
              <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Owner</Form.Label>
              <Form.Select
                value={filterOwner}
                onChange={(e) => setFilterOwner(e.target.value)}
                className="shadow-none"
                style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
              >
                <option value="">All Owners</option>
                {uniqueOwners.map(owner => (
                  <option key={owner} value={owner}>{owner}</option>
                ))}
              </Form.Select>
            </div>
          )}

          <div className="col-6 col-md-2">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>State</Form.Label>
            <Form.Select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              className="shadow-none"
              style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
            >
              <option value="">All States</option>
              {uniqueStates.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </Form.Select>
          </div>
        </div>

        {/* Row 2: City, Sort By, Clear Filters */}
        <div className="row g-3 align-items-end">
          <div className="col-6 col-md-2">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>City</Form.Label>
            <Form.Select
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="shadow-none"
              style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
            >
              <option value="">All Cities</option>
              {uniqueCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </Form.Select>
          </div>

          <div className="col-6 col-md-2">
            <Form.Label className="mb-2" style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>Sort By</Form.Label>
            <Form.Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="shadow-none"
              style={{ borderRadius: "8px", fontSize: "13px", color: "#475569", cursor: "pointer", borderColor: "#e2e8f0", height: "38px" }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="alpha_asc">Alphabetical (A-Z)</option>
              <option value="alpha_desc">Alphabetical (Z-A)</option>
            </Form.Select>
          </div>

          <div className="col-12 col-md d-flex justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={clearFilters}
              className="bg-white shadow-sm"
              style={{ 
                borderRadius: "8px", 
                border: "1px solid #e2e8f0", 
                color: "#475569",
                fontWeight: "600", 
                fontSize: "12px", 
                padding: "8px 24px",
                height: "38px"
              }}
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>
          <strong>
            {selectedBoat?.boatListed ? "Disable" : "Enable"} Listing
          </strong>
        </DialogTitle>
        <DialogContent>
          {selectedBoat?.boatListed
            ? `Are you sure you want to disable this boat? It will be removed from the boat listings but can be re-enabled later.`
            : `Are you sure you want to enable this boat? It will appear in the boat listings.`}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setSelectedBoat({});
            }}
            variant="danger"
            className="text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={async (e) => {
              e.stopPropagation();
              await UpdateListingState(selectedBoat?.id, !selectedBoat?.boatListed);
              setOpen(false);
              setSelectedBoat({});
            }}
            variant="primary"
          >
            {updateListingLoading ? (
              <ThreeDots
                visible={true}
                height="20"
                width="50"
                color="white"
                radius="9"
                ariaLabel="three-dots-loading"
              />
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateOpen} onClose={() => setDuplicateOpen(false)}>
        <DialogTitle>
          <strong>Duplicate Boat</strong>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to duplicate this boat? A copy will be created with "(Copy)" added to the name and set to Pending status.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDuplicateOpen(false);
              setBoatToDuplicate(null);
            }}
            variant="danger"
            className="text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDuplicateBoat}
            variant="primary"
          >
            {duplicateLoading ? (
              <ThreeDots
                visible={true}
                height="20"
                width="50"
                color="white"
                radius="9"
                ariaLabel="three-dots-loading"
              />
            ) : (
              "Duplicate"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Desktop/Tablet Table View */}
      <div className="table-responsive d-none d-md-block">
        <Table hover {...getTableProps()} style={{ verticalAlign: "middle" }}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps()}
                    style={{
                      color: "#64748b",
                      fontSize: "12px",
                      fontWeight: "600",
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #e2e8f0",
                      paddingTop: "16px",
                      paddingBottom: "16px"
                    }}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  style={{ borderBottom: "1px solid #f1f5f9" }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      style={{ padding: "16px 12px", borderTop: "none" }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </Table>
        {page.length === 0 && (
          <div className="text-center py-5 text-muted">
            No listings found matching your criteria.
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="d-block d-md-none">
        {page.map((row) => {
          prepareRow(row);
          const { id, name, year, backgroundImage, status, boatListed } = row.original;
          
          let badgeColor = "#e2e8f0";
          let textColor = "#475569";
          let dotColor = "#94a3b8";

          if (status?.toLowerCase() === "active" || (typeof status === "object" && status?.featured)) {
            badgeColor = "#dcfce7";
            textColor = "#166534";
            dotColor = "#22c55e";
          } else if (status?.toLowerCase() === "pending") {
            badgeColor = "#fef08a";
            textColor = "#854d0e";
            dotColor = "#eab308";
          } else if (status?.toLowerCase() === "reject") {
            badgeColor = "#fee2e2";
            textColor = "#991b1b";
            dotColor = "#ef4444";
          } else if (typeof status === "string") {
            if (status?.includes("Active") || status?.includes("Featured")) {
              badgeColor = "#dcfce7"; textColor = "#166534"; dotColor = "#22c55e";
            } else if (status?.includes("Pending")) {
              badgeColor = "#fef08a"; textColor = "#854d0e"; dotColor = "#eab308";
            }
          }

          const displayValue = typeof status === "object" ? (status.featured ? "Featured" : "Not Featured") : status || "Pending";
          const formattedStatus = displayValue?.toLowerCase() === 'pending review' || displayValue?.toLowerCase() === 'pending' ? 'Pending' : displayValue;

          return (
            <div key={id} className="card mb-3 shadow-sm border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-start gap-3 mb-3">
                  <Link to={`/listing/${id}`}>
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "8px",
                        backgroundImage: `url(${getImageUrl(backgroundImage)})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "1px solid #dee2e6",
                        flexShrink: 0
                      }}
                    />
                  </Link>
                  <div className="flex-grow-1 min-width-0">
                    <Link to={`/listing/${id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <strong className="d-block text-truncate" style={{ fontSize: "16px", color: "#1e293b", marginBottom: "4px" }}>{name}</strong>
                    </Link>
                    <div style={{ fontSize: "13px", color: "#64748b", marginBottom: "8px" }}>
                      ID: B-{id.substring(id.length - 5).toUpperCase()}
                      {year !== "N/A" && <div>{year} Model</div>}
                    </div>
                    <div
                      style={{
                        backgroundColor: badgeColor,
                        color: textColor,
                        padding: "4px 10px",
                        borderRadius: "20px",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        whiteSpace: "nowrap"
                      }}
                    >
                      <div style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: dotColor, flexShrink: 0 }}></div>
                      {formattedStatus}
                    </div>
                  </div>
                </div>

                {isAdmin && row.original.ownerName && (
                  <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom" style={{ borderColor: "#f1f5f9" }}>
                    <span style={{ fontSize: "12px", fontWeight: "600", color: "#94a3b8", textTransform: "uppercase" }}>Owner:</span>
                    <Link to={`/dashboard/user/${row.original.ownerId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div className="d-flex align-items-center gap-2">
                        <UserAvatar
                          src={row.original.ownerAvatar}
                          name={row.original.ownerName}
                          size={24}
                          style={{ backgroundColor: "#334155", fontSize: "10px" }}
                        />
                        <strong style={{ fontSize: "13px", color: "#334155" }}>{row.original.ownerName}</strong>
                      </div>
                    </Link>
                  </div>
                )}

                <div className="d-flex gap-2 mt-2">
                  <Button
                    as={Link}
                    to={`/dashboard/listing/${id}`}
                    variant="light"
                    className="flex-fill d-flex align-items-center justify-content-center gap-1"
                    style={{ borderRadius: "8px", padding: "8px", fontSize: "13px", backgroundColor: "#f8fafc", color: "#475569", border: "1px solid #e2e8f0", textDecoration: "none" }}
                  >
                    <FontAwesomeIcon icon={faEdit} /> Edit
                  </Button>
                  <Button
                    variant="primary"
                    className="flex-fill d-flex align-items-center justify-content-center gap-1"
                    style={{ borderRadius: "8px", padding: "8px", fontSize: "13px" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setBoatToDuplicate({ id, name });
                      setDuplicateOpen(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faCopy} /> Duplicate
                  </Button>
                  <Button
                    variant={boatListed ? "danger" : "success"}
                    className="flex-fill d-flex align-items-center justify-content-center gap-1"
                    style={{
                      borderRadius: "8px",
                      padding: "8px",
                      fontSize: "13px",
                      backgroundColor: boatListed ? "#fee2e2" : "#dcfce7",
                      color: boatListed ? "#ef4444" : "#22c55e",
                      border: "none"
                    }}
                    disabled={updateListingLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setSelectedBoat(row.original);
                      setOpen(true);
                    }}
                  >
                    <FontAwesomeIcon icon={boatListed ? faBan : faCheckCircle} /> {boatListed ? "Disable" : "Enable"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {page.length === 0 && (
          <div className="text-center py-5 text-muted border rounded" style={{ backgroundColor: "#f8fafc" }}>
            No listings found matching your criteria.
          </div>
        )}
      </div>

      {page.length > 0 && (
        <Row className="mt-4 border-top pt-3">
          <Col xs={6} className='d-flex align-items-center'>
            <span className='text-muted' style={{ fontSize: "14px" }}>
              Showing {pageIndex * pageSize + 1} to {Math.min((pageIndex + 1) * pageSize, filteredData.length)} of {filteredData.length} entries
            </span>
          </Col>
          <Col xs={6} className="d-flex justify-content-end">
            <Button
              variant={canPreviousPage ? "primary" : "light"}
              className="m-1"
              style={{ fontSize: "14px", borderRadius: "8px" }}
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
            >
              Previous
            </Button>
            <Button
              variant={canNextPage ? "primary" : "light"}
              className="m-1"
              style={{ fontSize: "14px", borderRadius: "8px" }}
              onClick={() => nextPage()}
              disabled={!canNextPage}
            >
              Next
            </Button>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProductList;
